// apps/client/src/pages/HomePage.tsx
//
// Home Dashboard — V2 Beelucky Sweepstakes
// Golden Path 2: Context-aware smart CTA, Sunday banner, quick actions.
// All CTAs in the bottom 2/3 (Thumb Zone compliance).
//

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import { useUserStore } from '../store/userStore';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import { useTranslation } from 'react-i18next';
import Icon from '../ui-kit/Icon';
import { icChest3d, icGiftBox } from '../assets';
import { PerfectCoin } from '../ui-kit/OnboardingSVGs';
import { GlassCard } from '../ui-kit/GlassCard';
import { BottomNav } from '../ui-kit/Layouts';
import { FortuneWheelModal } from '../components/FortuneWheelModal';
import { api } from '../lib/api';

interface HomePageProps {
    onNavigate: (view: 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile') => void;
}



export function HomePage({ onNavigate }: HomePageProps) {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const { status, playerCount, tickets } = useGameStore();
    const { chips, missions, currentStreak, checkDailyStreak } = useUserStore();
    const [isWheelModalOpen, setIsWheelModalOpen] = useState(false);

    // Check streak on mount
    useEffect(() => {
        checkDailyStreak();
    }, [checkDailyStreak]);

    const dailyTimer = useGameStore((s) => s.dailyTimer);

    const displayName = user?.firstName ?? user?.username ?? 'Player';
    const unclaimedMissions = missions.filter(m => m.status === 'COMPLETED').length;
    const hasTickets = tickets.length > 0;

    // Animated BingoChips counter
    const animatedChips = useAnimatedCounter(chips);



    // Fetch nearest tournament for urgency banner
    const [nextTournament, setNextTournament] = useState<{
        id: string; title: string; startTime: string; prizePool: number;
        status: string; userTicketCount: number;
    } | null>(null);

    useEffect(() => {
        const fetchTournaments = async () => {
            try {
                const res = await api.getTournaments();
                if (res.ok && res.tournaments.length > 0) {
                    // Prefer LIVE, then earliest SCHEDULED
                    const live = res.tournaments.find(t => t.status === 'LIVE');
                    const next = live || res.tournaments.find(t => t.status === 'SCHEDULED');
                    if (next) {
                        setNextTournament({
                            id: next.id, title: next.title, startTime: next.startTime,
                            prizePool: next.prizePool, status: next.status,
                            userTicketCount: next.userTicketCount,
                        });
                    }
                }
            } catch { /* ignore */ }
        };
        fetchTournaments();
        const interval = setInterval(fetchTournaments, 30000);
        return () => clearInterval(interval);
    }, []);

    // Tournament countdown
    const [tournamentCountdown, setTournamentCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 });
    useEffect(() => {
        if (!nextTournament || nextTournament.status === 'LIVE') return;
        const update = () => {
            const diff = Math.max(0, Math.floor((new Date(nextTournament.startTime).getTime() - Date.now()) / 1000));
            setTournamentCountdown({
                days: Math.floor(diff / 86400),
                hours: Math.floor((diff % 86400) / 3600),
                minutes: Math.floor((diff % 3600) / 60),
                seconds: diff % 60,
                totalSeconds: diff,
            });
        };
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
    }, [nextTournament]);


    // Daily timer
    const timerMin = Math.floor((dailyTimer ?? 0) / 60);
    const timerSec = (dailyTimer ?? 0) % 60;
    const timerText = `${String(timerMin).padStart(2, '0')}:${String(timerSec).padStart(2, '0')}`;

    const handleNavAction = (tab: string) => {
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        type ValidView = typeof validViews[number];
        if (validViews.includes(tab as ValidView)) {
            onNavigate(tab as ValidView);
        } else {
            onNavigate('home');
        }
    };

    // Red dot on missions tab if unclaimed
    const notificationDots = unclaimedMissions > 0 ? ['missions'] : [];

    return (
        <div className="w-full min-h-screen bg-purple-gradient flex flex-col">
            {/* === HEADER (Read-only zone: avatar, name, balance) === */}
            <header className="px-4 pt-6 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onNavigate('profile')}
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FBBF24] to-[#F59E0B] flex items-center justify-center border-2 border-white/30 shadow-md"
                    >
                        <span className="text-[#1a0b2e] font-black text-sm">{displayName.charAt(0)}</span>
                    </motion.button>
                    <div>
                        <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">{t('home.bingoton')}</span>
                        <h1 className="text-xl font-display font-bold text-white">
                            {t('home.greeting', { name: displayName })}
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Daily Streak Pill */}
                    {currentStreak > 0 && (
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-red-500/20 px-2.5 py-1.5 rounded-pill flex items-center gap-1 border border-red-500/30 backdrop-blur-sm"
                        >
                            <span className="text-sm">🔥</span>
                            <span className="font-bold text-red-100 text-xs">
                                {currentStreak}
                            </span>
                        </motion.div>
                    )}

                    {/* BingoChips Pill with animated counter */}
                    <motion.div
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onNavigate('store:ofertas' as any)}
                        className="bg-gold-gradient px-4 py-2 rounded-pill flex items-center gap-2 shadow-glow-gold cursor-pointer"
                    >
                        <div className="w-6 h-6"><PerfectCoin /></div>
                        <span className="font-black text-[#1a0b2e]">{animatedChips.toLocaleString()}</span>
                        <Icon name="add" className="text-sm text-[#1a0b2e]/50" />
                    </motion.div>
                </div>
            </header>

            <main className="flex-1 px-4 pb-32 space-y-5 overflow-y-auto">
                {/* === BANNERS === */}
                <div className="space-y-3 mt-2">
                    {/* Game starts in < 15 mins banner */}
                    {status === 'WAITING' && dailyTimer > 0 && dailyTimer <= 900 && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/40 rounded-xl p-3 flex items-center justify-between cursor-pointer shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                            onClick={() => onNavigate('game')}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-3xl animate-bounce drop-shadow-[0_0_15px_rgba(52,211,153,0.8)]">⏱️</span>
                                <div>
                                    <p className="text-white font-black text-sm uppercase tracking-wider font-display">{t('home.gameStartsIn', { min: timerMin })}</p>
                                    <p className="text-emerald-300 font-bold text-xs mt-0.5">{t('home.grabCards')}</p>
                                </div>
                            </div>
                            <div className="bg-emerald-500/20 p-2 rounded-full shrink-0">
                                <Icon name="arrow_forward_ios" className="text-emerald-400 text-sm" />
                            </div>
                        </motion.div>
                    )}

                    {/* Tournament Urgency Banner — dynamic from API */}
                    {nextTournament && nextTournament.status === 'LIVE' && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-xl p-3 flex items-center justify-between cursor-pointer shadow-[0_0_20px_rgba(34,197,94,0.3)]"
                            onClick={() => onNavigate('sunday')}
                        >
                            <div className="flex items-center gap-3">
                                <motion.span
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ repeat: Infinity, duration: 1 }}
                                    className="text-3xl drop-shadow-[0_0_15px_rgba(34,197,94,0.8)]"
                                >🔴</motion.span>
                                <div>
                                    <p className="text-white font-black text-sm uppercase tracking-wider font-display">¡EN VIVO! {nextTournament.title}</p>
                                    <p className="text-green-300 font-bold text-xs mt-0.5">💰 Premio: {nextTournament.prizePool.toLocaleString()} — ¡Entra ahora!</p>
                                </div>
                            </div>
                            <div className="bg-green-500/20 p-2 rounded-full shrink-0">
                                <Icon name="arrow_forward_ios" className="text-green-400 text-sm" />
                            </div>
                        </motion.div>
                    )}

                    {nextTournament && nextTournament.status === 'SCHEDULED' && tournamentCountdown.totalSeconds > 0 && tournamentCountdown.totalSeconds <= 3600 && (
                        /* Less than 1 hour — RED URGENCY */
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/40 rounded-xl p-3 flex items-center justify-between cursor-pointer shadow-[0_0_20px_rgba(239,68,68,0.3)] animate-pulse"
                            onClick={() => onNavigate('sunday')}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-3xl animate-bounce drop-shadow-[0_0_15px_rgba(239,68,68,0.8)]">⏱️</span>
                                <div>
                                    <p className="text-white font-black text-sm uppercase tracking-wider font-display">
                                        ¡{tournamentCountdown.minutes}m {tournamentCountdown.seconds}s! {nextTournament.title}
                                    </p>
                                    <p className="text-red-300 font-bold text-xs mt-0.5">
                                        {nextTournament.userTicketCount > 0
                                            ? `✅ Tienes ${nextTournament.userTicketCount} cartón${nextTournament.userTicketCount > 1 ? 'es' : ''}`
                                            : `🎫 ¡Compra tu cartón antes de que empiece!`
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="bg-red-500/20 p-2 rounded-full shrink-0">
                                <Icon name="arrow_forward_ios" className="text-red-400 text-sm" />
                            </div>
                        </motion.div>
                    )}

                    {nextTournament && nextTournament.status === 'SCHEDULED' && tournamentCountdown.totalSeconds > 3600 && tournamentCountdown.totalSeconds <= 86400 && (
                        /* Less than 24 hours — YELLOW/GOLD URGENCY */
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-r from-[#FFC107]/20 to-[#FF9800]/20 border border-[#FFC107]/40 rounded-xl p-3 flex items-center justify-between cursor-pointer shadow-[0_0_15px_rgba(255,193,7,0.2)]"
                            onClick={() => onNavigate('sunday')}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-3xl animate-pulse drop-shadow-[0_0_15px_rgba(255,193,7,0.8)]">🌟</span>
                                <div>
                                    <p className="text-white font-black text-sm uppercase tracking-wider font-display">
                                        {tournamentCountdown.hours}h {tournamentCountdown.minutes}m — {nextTournament.title}
                                    </p>
                                    <p className="text-[#FFC107] font-bold text-xs mt-0.5">
                                        💰 Premio: {nextTournament.prizePool.toLocaleString()} — ¡No te lo pierdas!
                                    </p>
                                </div>
                            </div>
                            <div className="bg-[#FFC107]/20 p-2 rounded-full shrink-0">
                                <Icon name="arrow_forward_ios" className="text-[#FFC107] text-sm" />
                            </div>
                        </motion.div>
                    )}

                    {nextTournament && nextTournament.status === 'SCHEDULED' && tournamentCountdown.totalSeconds > 86400 && (
                        /* More than 24 hours — subtle purple */
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-r from-purple-500/15 to-indigo-500/15 border border-purple-500/30 rounded-xl p-3 flex items-center justify-between cursor-pointer"
                            onClick={() => onNavigate('sunday')}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-3xl">🏆</span>
                                <div>
                                    <p className="text-white font-black text-sm uppercase tracking-wider font-display">
                                        {nextTournament.title}
                                    </p>
                                    <p className="text-purple-300 font-bold text-xs mt-0.5">
                                        📅 En {tournamentCountdown.days}d {tournamentCountdown.hours}h — 💰 {nextTournament.prizePool.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            <div className="bg-purple-500/20 p-2 rounded-full shrink-0">
                                <Icon name="arrow_forward_ios" className="text-purple-400 text-sm" />
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* === 2. CONTEXT-AWARE DAILY CTA (The Smart Button) === */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <GlassCard
                        variant="default"
                        className="p-5 cursor-pointer active:scale-[0.98] transition-transform"
                        onClick={() => onNavigate('game')}
                    >
                        <div className="flex items-center gap-4">
                            {/* Icon */}
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${status === 'PLAYING'
                                ? 'bg-gradient-to-br from-red-500 to-red-600 animate-pulse'
                                : hasTickets
                                    ? 'bg-gradient-to-br from-emerald-400 to-emerald-500'
                                    : 'bg-gold-gradient'
                                }`}>
                                <Icon
                                    name={status === 'PLAYING' ? 'live_tv' : hasTickets ? 'check_circle' : 'confirmation_number'}
                                    className="text-3xl text-white drop-shadow-md"
                                />
                            </div>

                            {/* Text */}
                            <div className="flex-1">
                                <h3 className="font-black text-white text-lg font-display leading-tight drop-shadow-sm">
                                    {status === 'PLAYING'
                                        ? t('home.liveNow')
                                        : hasTickets
                                            ? t('home.readyToPlay')
                                            : t('home.getCards')}
                                </h3>
                                <p className="text-white/60 text-sm">
                                    {status === 'PLAYING'
                                        ? t('home.gameInProgress', { count: playerCount })
                                        : t('home.nextGameIn', { time: timerText })
                                    }
                                </p>
                            </div>

                            {/* Arrow */}
                            <Icon name="arrow_forward_ios" className="text-white/30 text-lg" />
                        </div>
                    </GlassCard>
                </motion.section>

                {/* === 3. QUICK ACTIONS (3-col) === */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="grid grid-cols-2 lg:grid-cols-3 gap-3"
                >
                    {/* Fortune Wheel / Daily Spin */}
                    <GlassCard
                        variant="default"
                        className="p-4 cursor-pointer active:scale-95 transition-all relative col-span-2 lg:col-span-1"
                        onClick={() => setIsWheelModalOpen(true)}
                    >
                        {/* Notification dot if free spin is available (we'll implement this properly later) */}
                        <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-3 shadow-sm">
                            <span className="text-2xl drop-shadow-md">🎡</span>
                        </div>
                        <span className="font-bold text-white text-sm leading-tight block drop-shadow-sm">
                            {t('home.dailySpin')}
                        </span>
                        <span className="text-white/50 text-xs">
                            {t('home.winFreePrizes')}
                        </span>
                    </GlassCard>

                    {/* Free BingoChips (→ Missions) */}
                    <GlassCard
                        variant="default"
                        className="p-4 cursor-pointer active:scale-95 transition-all relative"
                        onClick={() => onNavigate('missions')}
                    >
                        {/* Red dot */}
                        {unclaimedMissions > 0 && (
                            <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                        )}
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-3 shadow-sm">
                            <img src={icChest3d} alt="" className="w-7 h-7" />
                        </div>
                        <span className="font-bold text-white text-sm leading-tight block drop-shadow-sm">
                            {t('home.freeChips')}
                        </span>
                        <span className="text-white/50 text-xs">
                            {unclaimedMissions > 0 ? t('home.readyCount', { count: unclaimedMissions }) : t('home.completeTasks')}
                        </span>
                    </GlassCard>

                    {/* Invite Friends (→ Referrals) */}
                    <GlassCard
                        variant="default"
                        className="p-4 cursor-pointer active:scale-95 transition-all"
                        onClick={() => onNavigate('referrals')}
                    >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-500 flex items-center justify-center mb-3 shadow-sm">
                            <img src={icGiftBox} alt="" className="w-7 h-7" />
                        </div>
                        <span className="font-bold text-white text-sm leading-tight block drop-shadow-sm">
                            {t('home.inviteFriends')}
                        </span>
                        <span className="text-white/50 text-xs">{t('home.earnBonus')}</span>
                    </GlassCard>
                </motion.section>

                {/* === 4. PLAYER COUNT PILL === */}
                {playerCount > 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-center"
                    >
                        <div className="flex items-center gap-2 px-4 py-2 rounded-pill bg-white/10 border border-white/10 backdrop-blur-md">
                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                            <span className="text-xs font-bold text-white/60">
                                {t('home.playersOnline', { count: playerCount })}
                            </span>
                        </div>
                    </motion.div>
                )}
            </main>

            {/* Bottom Nav */}
            <BottomNav
                activeTab="home"
                onTabChange={handleNavAction}
                notificationDots={notificationDots}
            />

            <FortuneWheelModal
                isOpen={isWheelModalOpen}
                onClose={() => setIsWheelModalOpen(false)}
            />
        </div>
    );
}
